import { type QueryKey, skipToken, useQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Agents } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { isWorkspaceAgentSessionUnresolvableError } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionUnresolvableError';

export const PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST_KEY = 'PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST';
const ARTIFACT_TRANSIENT_RETRIES = 1;

export type PrivateWorkspaceAgentSessionArtifactsListParams = Omit<
  NonNullable<Parameters<typeof Agents.Sessions.Artifacts.list>[0]>,
  'agentType' | 'artifactIds' | 'cursor' | 'limit' | 'sessionId' | 'workspaceId'
> & {
  artifactIds: readonly string[];
  sessionId: string | undefined;
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentSessionArtifactsListKey = [
  typeof PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST_KEY,
  PrivateWorkspaceAgentSessionArtifactsListParams,
];

export const getPrivateWorkspaceAgentSessionArtifactsListKey = (
  params: PrivateWorkspaceAgentSessionArtifactsListParams,
): PrivateWorkspaceAgentSessionArtifactsListKey => [PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST_KEY, params];

export const isPrivateWorkspaceAgentSessionArtifactsListKey = (
  key: QueryKey,
): key is PrivateWorkspaceAgentSessionArtifactsListKey =>
  Array.isArray(key) && key[0] === PRIVATE_WORKSPACE_AGENT_SESSION_ARTIFACTS_LIST_KEY && isObject(key[1]);

export type UsePrivateWorkspaceAgentSessionArtifactsListOptions = {
  enabled?: boolean;
};

export const usePrivateWorkspaceAgentSessionArtifactsList = (
  params: PrivateWorkspaceAgentSessionArtifactsListParams,
  options?: UsePrivateWorkspaceAgentSessionArtifactsListOptions,
) => {
  const { artifactIds, sessionId, workspaceId } = params;
  const canFetch = Boolean(workspaceId) && Boolean(sessionId) && artifactIds.length > 0;

  return useQuery({
    enabled: canFetch && (options?.enabled ?? true),
    queryFn:
      workspaceId && sessionId && artifactIds.length > 0
        ? () =>
            callAirApi(() =>
              Agents.Sessions.Artifacts.list({
                agentType: 'workspace',
                artifactIds: [...artifactIds],
                sessionId,
                workspaceId,
              }),
            )
        : skipToken,
    queryKey: getPrivateWorkspaceAgentSessionArtifactsListKey(params),
    retry: (failureCount, error) =>
      !isWorkspaceAgentSessionUnresolvableError(error) && failureCount < ARTIFACT_TRANSIENT_RETRIES,
  });
};
